import React, { useState, useCallback } from 'react';
import { PRIORITY_OPTIONS, SOURCE_OPTIONS } from '@/hooks/useLeads';
import { Save, X } from 'lucide-react';

const EMPTY_LEAD = {
  company: { name: '', plz: '', city: '', street: '', industry: '', annual_consumption_kwh: '', current_price_ct_kwh: '', current_supplier: '' },
  contact: { first_name: '', last_name: '', email: '', phone: '', position: '' },
  source: 'Kaltakquise',
  priority: 'normal',
  notes: '',
  next_action: '',
  next_action_date: '',
};

// Field component OUTSIDE of LeadForm to prevent re-creation on every render
// (which would cause inputs to lose focus on every keystroke)
const Field = ({ label, error, required, children }) => (
  <div>
    <label className="block text-sm font-semibold text-foreground mb-1.5">
      {label} {required && <span className="text-destructive">*</span>}
    </label>
    {children}
    {error && <p className="text-xs text-destructive mt-1">{error}</p>}
  </div>
);

const inputCls = (hasError) =>
  `w-full px-3 py-2.5 bg-background border rounded-lg text-sm transition-colors focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/20 ${
    hasError ? 'border-destructive bg-destructive/5' : 'border-border'
  }`;

const LeadForm = ({ initialData, onSave, onCancel }) => {
  const [form, setForm] = useState(initialData || EMPTY_LEAD);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);

  const updateCompany = useCallback((field, value) => {
    setForm(f => ({ ...f, company: { ...f.company, [field]: value } }));
    setErrors(e => {
      if (!e[`company.${field}`]) return e;
      const next = { ...e };
      delete next[`company.${field}`];
      return next;
    });
  }, []);

  const updateContact = useCallback((field, value) => {
    setForm(f => ({ ...f, contact: { ...f.contact, [field]: value } }));
    setErrors(e => {
      if (!e[`contact.${field}`]) return e;
      const next = { ...e };
      delete next[`contact.${field}`];
      return next;
    });
  }, []);

  const updateField = useCallback((field, value) => {
    setForm(f => ({ ...f, [field]: value }));
  }, []);

  const validate = () => {
    const errs = {};
    if (!form.company.name.trim()) errs['company.name'] = 'Firmenname erforderlich';
    if (!form.company.plz.trim()) errs['company.plz'] = 'PLZ erforderlich';
    if (form.company.plz && !/^\d{5}$/.test(form.company.plz.trim())) errs['company.plz'] = 'PLZ muss 5 Ziffern haben';
    if (!form.contact.last_name.trim()) errs['contact.last_name'] = 'Nachname erforderlich';
    if (form.company.annual_consumption_kwh && isNaN(Number(form.company.annual_consumption_kwh))) errs['company.annual_consumption_kwh'] = 'Ungueltige Zahl';
    if (form.company.current_price_ct_kwh && isNaN(Number(form.company.current_price_ct_kwh))) errs['company.current_price_ct_kwh'] = 'Ungueltige Zahl';
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setSaving(true);
    try {
      const data = {
        ...form,
        company: {
          ...form.company,
          annual_consumption_kwh: form.company.annual_consumption_kwh ? Number(form.company.annual_consumption_kwh) : null,
          current_price_ct_kwh: form.company.current_price_ct_kwh ? Number(form.company.current_price_ct_kwh) : null,
        },
      };
      await onSave(data);
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Firmendaten */}
      <div>
        <h3 className="text-xs font-bold text-muted-foreground uppercase tracking-wider mb-3">Firmendaten</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label="Firmenname" error={errors['company.name']} required>
            <input
              value={form.company.name}
              onChange={e => updateCompany('name', e.target.value)}
              className={inputCls(!!errors['company.name'])}
              placeholder="Mueller GmbH"
            />
          </Field>
          <Field label="Branche">
            <input
              value={form.company.industry}
              onChange={e => updateCompany('industry', e.target.value)}
              className={inputCls(false)}
              placeholder="z.B. Metallverarbeitung"
            />
          </Field>
          <Field label="Strasse">
            <input
              value={form.company.street}
              onChange={e => updateCompany('street', e.target.value)}
              className={inputCls(false)}
              placeholder="Musterstr. 1"
            />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="PLZ" error={errors['company.plz']} required>
              <input
                value={form.company.plz}
                onChange={e => updateCompany('plz', e.target.value)}
                className={inputCls(!!errors['company.plz'])}
                placeholder="10115"
                maxLength={5}
              />
            </Field>
            <Field label="Stadt">
              <input
                value={form.company.city}
                onChange={e => updateCompany('city', e.target.value)}
                className={inputCls(false)}
                placeholder="Berlin"
              />
            </Field>
          </div>
          <Field label="Jahresverbrauch (kWh)" error={errors['company.annual_consumption_kwh']}>
            <input
              type="number"
              value={form.company.annual_consumption_kwh}
              onChange={e => updateCompany('annual_consumption_kwh', e.target.value)}
              className={inputCls(!!errors['company.annual_consumption_kwh'])}
              placeholder="250000"
            />
          </Field>
          <Field label="Aktueller Preis (ct/kWh)" error={errors['company.current_price_ct_kwh']}>
            <input
              type="number"
              step="0.01"
              value={form.company.current_price_ct_kwh}
              onChange={e => updateCompany('current_price_ct_kwh', e.target.value)}
              className={inputCls(!!errors['company.current_price_ct_kwh'])}
              placeholder="12.50"
            />
          </Field>
          <Field label="Aktueller Versorger">
            <input
              value={form.company.current_supplier}
              onChange={e => updateCompany('current_supplier', e.target.value)}
              className={inputCls(false)}
              placeholder="z.B. Stadtwerke"
            />
          </Field>
        </div>
      </div>

      {/* Ansprechpartner */}
      <div>
        <h3 className="text-xs font-bold text-muted-foreground uppercase tracking-wider mb-3">Ansprechpartner</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label="Vorname">
            <input
              value={form.contact.first_name}
              onChange={e => updateContact('first_name', e.target.value)}
              className={inputCls(false)}
              placeholder="Hans"
            />
          </Field>
          <Field label="Nachname" error={errors['contact.last_name']} required>
            <input
              value={form.contact.last_name}
              onChange={e => updateContact('last_name', e.target.value)}
              className={inputCls(!!errors['contact.last_name'])}
              placeholder="Mueller"
            />
          </Field>
          <Field label="E-Mail">
            <input
              type="email"
              value={form.contact.email}
              onChange={e => updateContact('email', e.target.value)}
              className={inputCls(false)}
              placeholder="h.mueller@firma.de"
            />
          </Field>
          <Field label="Telefon">
            <input
              value={form.contact.phone}
              onChange={e => updateContact('phone', e.target.value)}
              className={inputCls(false)}
              placeholder="+49 30 12345678"
            />
          </Field>
          <Field label="Position">
            <input
              value={form.contact.position}
              onChange={e => updateContact('position', e.target.value)}
              className={inputCls(false)}
              placeholder="z.B. Geschaeftsfuehrer"
            />
          </Field>
        </div>
      </div>

      {/* Vertriebsdaten */}
      <div>
        <h3 className="text-xs font-bold text-muted-foreground uppercase tracking-wider mb-3">Vertrieb</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Field label="Quelle">
            <select
              value={form.source}
              onChange={e => updateField('source', e.target.value)}
              className={inputCls(false)}
            >
              {SOURCE_OPTIONS.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          </Field>
          <Field label="Prioritaet">
            <select
              value={form.priority}
              onChange={e => updateField('priority', e.target.value)}
              className={inputCls(false)}
            >
              {PRIORITY_OPTIONS.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
            </select>
          </Field>
          <Field label="Naechste Aktion am">
            <input
              type="date"
              value={form.next_action_date}
              onChange={e => updateField('next_action_date', e.target.value)}
              className={inputCls(false)}
            />
          </Field>
        </div>
        <div className="mt-4">
          <Field label="Naechste Aktion">
            <input
              value={form.next_action}
              onChange={e => updateField('next_action', e.target.value)}
              className={inputCls(false)}
              placeholder="z.B. Angebot erstellen"
            />
          </Field>
        </div>
        <div className="mt-4">
          <Field label="Notizen">
            <textarea
              value={form.notes}
              onChange={e => updateField('notes', e.target.value)}
              className={inputCls(false) + ' h-24 resize-none'}
              placeholder="Interne Notizen zum Lead..."
            />
          </Field>
        </div>
      </div>

      {/* Buttons */}
      <div className="flex items-center justify-end gap-3 pt-4 border-t border-border">
        {onCancel && (
          <button
            type="button"
            onClick={onCancel}
            className="flex items-center gap-2 px-4 py-2.5 border border-border rounded-lg text-sm font-semibold text-foreground hover:bg-muted transition-colors"
          >
            <X size={16} />
            Abbrechen
          </button>
        )}
        <button
          type="submit"
          disabled={saving}
          className="flex items-center gap-2 bg-primary text-primary-foreground px-5 py-2.5 rounded-lg text-sm font-semibold hover:bg-primary/90 transition-colors shadow-sm disabled:opacity-50"
        >
          <Save size={16} />
          {saving ? 'Wird gespeichert...' : (initialData ? 'Speichern' : 'Lead anlegen')}
        </button>
      </div>
    </form>
  );
};

export default LeadForm;
